import { useState, useMemo, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { fetchOnboardingData, fetchOnboardingWeek } from "./graphService";

const C = {
  dark: "#1a1a2e",
  gold: "#c9a84c",
  white: "#fff",
  surface: "#f8f7f4",
  border: "#e5e7eb",
  muted: "#6b7280",
  text: "#374151",
  green: "#27ae60",
  orange: "#e67e22",
  red: "#c0392b",
  blue: "#2980b9",
};

const STATUS_CONFIG = {
  Green:  { color: "#27ae60", bg: "#d4edda", label: "Ready" },
  Yellow: { color: "#b45309", bg: "#fef3c7", label: "Almost Ready" },
  Orange: { color: "#c2410c", bg: "#ffedd5", label: "At Risk" },
  Gray:   { color: "#6b7280", bg: "#f3f4f6", label: "Non-Starter" },
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Gray;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      fontSize: 10, fontWeight: 700, padding: "2px 8px",
      borderRadius: 3, textTransform: "uppercase", letterSpacing: 0.5,
      whiteSpace: "nowrap",
    }}>{cfg.label}</span>
  );
}

function TypeBadge({ type }) {
  if (type === "NonStarter") return (
    <span style={{ background: "#f3f4f6", color: "#6b7280", fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3 }}>NS</span>
  );
  return (
    <span style={{
      background: type === "C2C" ? "#ede9fe" : "#dbeafe",
      color: type === "C2C" ? "#6d28d9" : "#1d4ed8",
      fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, whiteSpace: "nowrap",
    }}>{type}</span>
  );
}

function ProgressBar({ pct, status }) {
  const color = STATUS_CONFIG[status]?.color || "#6b7280";
  return (
    <div style={{ background: "#f3f4f6", borderRadius: 3, height: 6, width: 60, overflow: "hidden" }}>
      <div style={{ width: Math.round(pct * 100) + "%", background: color, height: "100%", borderRadius: 3 }} />
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 20px", color: "#6b7280", justifyContent: "center" }}>
      <div style={{
        width: 18, height: 18, border: "2px solid #e5e7eb",
        borderTop: "2px solid #c9a84c", borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <span style={{ fontSize: 13 }}>Loading onboarding data from SharePoint...</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function OnboardingTracker() {
  const { instance, accounts } = useMsal();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allWeeks, setAllWeeks] = useState([]);
  const [weekData, setWeekData] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterHire, setFilterHire] = useState("all");
  const [search, setSearch] = useState("");

  const getToken = useCallback(async function() {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.accessToken;
  }, [instance, accounts]);

  const loadData = useCallback(async function() {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await fetchOnboardingData(token);
      setAllWeeks(result.allWeeks);
      setWeekData(result.weekData);
      setLastUpdated(result.lastUpdated);
      if (result.weeks.length > 0) {
        setSelectedWeek(function(prev) { return prev || result.weeks[0]; });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(function() { loadData(); }, []);

  async function handleWeekChange(week) {
    setSelectedWeek(week);
    setFilterStatus("all");
    setFilterType("all");
    setFilterHire("all");
    setSearch("");
    if (!weekData[week]) {
      setLoadingWeek(true);
      try {
        const token = await getToken();
        const rows = await fetchOnboardingWeek(token, week);
        setWeekData(function(prev) { return { ...prev, [week]: rows }; });
      } catch (e) {
        setWeekData(function(prev) { return { ...prev, [week]: [] }; });
      } finally {
        setLoadingWeek(false);
      }
    }
  }

  const currentData = (selectedWeek && weekData[selectedWeek]) || [];

  const filtered = useMemo(function() {
    return currentData.filter(function(row) {
      if (filterStatus !== "all" && row.status !== filterStatus) return false;
      if (filterType !== "all" && row.type !== filterType) return false;
      if (filterHire !== "all" && row.hire !== filterHire) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          row.candidate.toLowerCase().includes(q) ||
          row.client.toLowerCase().includes(q) ||
          row.recruiter.toLowerCase().includes(q) ||
          (row.salesRep || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [currentData, filterStatus, filterType, filterHire, search]);

  const counts = useMemo(function() {
    return {
      total: currentData.length,
      green: currentData.filter(function(r) { return r.status === "Green"; }).length,
      yellow: currentData.filter(function(r) { return r.status === "Yellow"; }).length,
      orange: currentData.filter(function(r) { return r.status === "Orange"; }).length,
      gray: currentData.filter(function(r) { return r.status === "Gray"; }).length,
      w2: currentData.filter(function(r) { return r.type === "W2"; }).length,
      c2c: currentData.filter(function(r) { return r.type === "C2C"; }).length,
      needsAction: currentData.filter(function(r) { return r.status === "Yellow" || r.status === "Orange"; }).length,
    };
  }, [currentData]);

  if (loading) return <Spinner />;

  if (error) return (
    <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "16px 20px" }}>
      <div style={{ fontWeight: 700, color: "#c0392b", marginBottom: 6 }}>Failed to load onboarding data</div>
      <div style={{ fontSize: 12, color: "#7f1d1d", marginBottom: 12 }}>{error}</div>
      <button onClick={loadData} style={{ padding: "6px 14px", background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer" }}>
        Retry
      </button>
    </div>
  );

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", fontSize: 13 }}>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ flex: "0 0 auto" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Week Ending</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <select
              value={selectedWeek || ""}
              onChange={function(e) { handleWeekChange(e.target.value); }}
              style={{ padding: "7px 12px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, fontWeight: 600, color: "#1a1a2e", background: "#fff", cursor: "pointer", outline: "none" }}
            >
              {allWeeks.map(function(w) { return <option key={w} value={w}>{w}</option>; })}
            </select>
            <button onClick={loadData} title="Refresh from SharePoint" style={{ padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13, color: "#6b7280" }}>↻</button>
          </div>
          {lastUpdated && <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4 }}>Updated {new Date(lastUpdated).toLocaleTimeString()}</div>}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: 1 }}>
          {[
            { label: "Total", value: counts.total, color: "#1a1a2e" },
            { label: "Ready", value: counts.green, color: "#27ae60" },
            { label: "Almost Ready", value: counts.yellow, color: "#b45309" },
            { label: "At Risk", value: counts.orange, color: "#c2410c" },
            { label: "Non-Starters", value: counts.gray, color: "#6b7280" },
            { label: "W2", value: counts.w2, color: "#2980b9" },
            { label: "C2C", value: counts.c2c, color: "#6d28d9" },
          ].map(function(card) {
            return (
              <div key={card.label} style={{ background: "#fff", border: "1px solid #e5e7eb", borderTop: "3px solid " + card.color, borderRadius: 6, padding: "8px 14px", minWidth: 64, textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>{card.value}</div>
                <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{card.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {counts.needsAction > 0 && (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderLeft: "4px solid #f59e0b", borderRadius: 6, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#b45309", fontWeight: 700, fontSize: 14 }}>!</span>
          <span style={{ fontSize: 12, color: "#78350f" }}>
            <strong>{counts.needsAction} candidate{counts.needsAction !== 1 ? "s" : ""}</strong> need attention before starting this week.
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input type="text" placeholder="Search candidate, client, recruiter..." value={search} onChange={function(e) { setSearch(e.target.value); }}
          style={{ flex: "1 1 200px", padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, outline: "none", color: "#374151" }} />
        {[
          { label: "Status", key: "status", val: filterStatus, set: setFilterStatus, opts: ["all", "Green", "Yellow", "Orange", "Gray"] },
          { label: "Type", key: "type", val: filterType, set: setFilterType, opts: ["all", "W2", "C2C"] },
          { label: "Hire Type", key: "hire", val: filterHire, set: setFilterHire, opts: ["all", "New Hire", "Rehire"] },
        ].map(function(f) {
          return (
            <select key={f.key} value={f.val} onChange={function(e) { f.set(e.target.value); }}
              style={{ padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, color: "#374151", background: "#fff", cursor: "pointer" }}>
              {f.opts.map(function(o) { return <option key={o} value={o}>{o === "all" ? f.label + ": All" : o}</option>; })}
            </select>
          );
        })}
        {(search || filterStatus !== "all" || filterType !== "all" || filterHire !== "all") && (
          <button onClick={function() { setSearch(""); setFilterStatus("all"); setFilterType("all"); setFilterHire("all"); }}
            style={{ padding: "6px 12px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, color: "#6b7280", background: "#fff", cursor: "pointer" }}>Clear</button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280", padding: "6px 0" }}>{filtered.length} of {currentData.length} records</div>
      </div>

      {loadingWeek ? <Spinner /> : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                {["Status", "Candidate", "Type", "Start Date", "Client", "Recruiter", "Sales Rep", "Hire", "Progress", "BH Status", "Notes"].map(function(h) {
                  return <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>{h}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} style={{ padding: "32px", textAlign: "center", color: "#6b7280" }}>
                  {currentData.length === 0 ? "No data available for this week." : "No records match your filters."}
                </td></tr>
              )}
              {filtered.map(function(row, i) {
                const isAtRisk = row.status === "Orange" || row.status === "Yellow";
                const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.Gray;
                return (
                  <tr key={row.assignment + i} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "#fff" : "#fafafa", borderLeft: isAtRisk ? "3px solid " + cfg.color : "3px solid transparent" }}>
                    <td style={{ padding: "9px 12px" }}><StatusPill status={row.status} /></td>
                    <td style={{ padding: "9px 12px", fontWeight: 600, color: "#1a1a2e", maxWidth: 180 }}>
                      <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.candidate}</div>
                      {row.assignment && <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 400 }}>#{row.assignment}</div>}
                    </td>
                    <td style={{ padding: "9px 12px" }}><TypeBadge type={row.type} /></td>
                    <td style={{ padding: "9px 12px", color: "#6b7280", whiteSpace: "nowrap", fontSize: 11 }}>{row.startDate || "—"}</td>
                    <td style={{ padding: "9px 12px", color: "#374151", maxWidth: 160 }}>
                      <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.client}</div>
                    </td>
                    <td style={{ padding: "9px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>{row.recruiter || "—"}</td>
                    <td style={{ padding: "9px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>{row.salesRep || "—"}</td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, fontWeight: 600, background: row.hire === "Rehire" ? "#f3f4f6" : "#eff6ff", color: row.hire === "Rehire" ? "#6b7280" : "#1d4ed8" }}>{row.hire || "—"}</span>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <ProgressBar pct={row.pct} status={row.status} />
                        <span style={{ fontSize: 10, color: "#6b7280", whiteSpace: "nowrap" }}>{Math.round(row.pct * 100)}%</span>
                      </div>
                    </td>
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: row.bhStatus === "Ready to Start" ? "#27ae60" : row.bhStatus === "NonStarter" ? "#6b7280" : "#e67e22" }}>
                        {row.bhStatus || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "9px 12px", maxWidth: 200 }}>
                      {row.notes ? (
                        <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", padding: "2px 6px", borderRadius: 3, display: "inline-block", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.notes}>{row.notes}</span>
                      ) : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11, color: "#6b7280" }}>
        Live data from SharePoint · External New Hire Onboarding Tracker
        {lastUpdated && " · Last refreshed " + new Date(lastUpdated).toLocaleTimeString()}
      </div>
    </div>
  );
}
